import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login({ email, password });
      if (u?.role === "staff") navigate("/staff-portal");
      else if (u?.account_type === "personal") navigate("/pa");
      else navigate("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-1.5 justify-center mb-8" data-testid="auth-logo">
          <img src="/operamind-mark.png" alt="OperaMind" className="h-11 w-11 object-contain" />
          <div className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-xl tracking-tight text-slate-900">Opera<span className="text-blue-600">Mind</span></span>
            <span className="mt-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-900">Intelligence. Orchestrated.</span>
          </div>
        </Link>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-bold tracking-tight">Welcome back</h1>
          <p className="text-slate-500 text-sm mt-1">Sign in to your OperaMind dashboard.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs">Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" placeholder="you@business.com" />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" placeholder="••••••••" />
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-blue-600 hover:underline mt-1.5 inline-block">Forgot password?</Link>
            </div>
            {err && <p data-testid="login-error" className="text-sm text-red-600">{err}</p>}
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            New to OperaMind? <Link to="/pricing" className="text-blue-600 font-medium hover:underline">Choose a plan</Link>
          </p>
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">Demo login: demo@operamind.com / demo123</p>
      </div>
    </div>
  );
}
